import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { reportDataPatientIssues } from '@constants/reports/report-patient-issues';
import { schedulesOptions } from '@constants/schedules';
import { SelectOption } from '@interfaces/input-select-option.interface';
import {
    PatientPharma,
    Report,
} from '@interfaces/report/report.interface';
import { Utils } from '@json/src/app/Utils';
import { CallsService } from '@services/api/calls.service';
import { DateService } from '@services/date.service';
import { DocumentService } from '@services/document.service';
import { InstitutionService } from '@services/institution.service';
import { ReportsService } from '@services/reports/reports.service';
import { RoleManagerService } from '@services/role-manager.service';
import { Title } from '@angular/platform-browser';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import moment from 'moment';
import { LoggerService } from '@services/logger.service';
import { UserAction } from '@enums/user-action';

@Component({
    selector: 'app-report-patient-issues',
    templateUrl: './report-patient-issues.component.html',
    styleUrls: ['./report-patient-issues.component.scss'],
})
export class ReportPatientIssuesComponent {
    public reportDataPatientIssues = reportDataPatientIssues;
    public showPatientSelect: boolean = false;
    public showIntervalDay: boolean = false;
    public showSpecificDaySelect: boolean = false;
    public currentInstitution: number;
    public form: FormGroup;
    public currentRoute: string;
    public report: Report;
    public pathReportDownload: string;
    public safeURL: SafeUrl;
    public selectOptionsPatients: SelectOption[];
    public schedulesOptions = schedulesOptions;

    public selectOptionsInsitutions: SelectOption[];
    public typeGlobal: boolean = false;

    constructor(
        private router: Router,
        private formBuilder: FormBuilder,
        private dateService: DateService,
        private calls: CallsService,
        private reportService: ReportsService,
        private documentService: DocumentService,
        private institutionService: InstitutionService,
        private roleManager: RoleManagerService,
        private titleService: Title,
        private gaService: GoogleAnalyticsService,
        private logger: LoggerService
    ) {
        this.form = this.formBuilder.group({
            specificDay: [moment()],
            startDate: [moment()],
            endDate: [moment().add(1, 'week')],
            selectedPatient: [''],
            intervalType: ['specificDay'],
            selectedOptionFrom: ['00:00'],
            selectedOptionUntil: ['23:00'],
            selectedInstitution: ['', Validators.required],
        });
        this.currentRoute = this.router.url;
        this.currentInstitution = this.institutionService.getCurrentInstitution();
    }

    ngOnInit(): void {
        this.titleService.setTitle(`Reporte: Incidencias de pacientes`);
        this.gaService.pageView(`${window.location.href}`, `Reporte: Incidencias de pacientes`);
        this.logger.logPage('Reporte: Incidencias de pacientes');

        this.typeGlobal = this.roleManager.isPharma() || this.roleManager.isHospitalPharma();

        this.getDataForTheSelects();
        this.setListenerSelectDay();
        this.setListenerInstitution();
    }

    setListenerSelectDay(): void {
        this.form.get('intervalType')?.valueChanges.subscribe((value) => {
            if (value === 'specificDay') {
                this.showSpecificDaySelect = true;
                this.showIntervalDay = false;
            } else if (value === 'intervalDay') {
                this.showSpecificDaySelect = false;
                this.showIntervalDay = true;
            }
        });
    }

    setListenerInstitution(): void {
        this.form.get('selectedInstitution')?.valueChanges.subscribe((value) => {
            if (value && value.id) {
                this.reportService
                    .getRepParamsEditDataMedchanges(value.id)
                    .subscribe((result) => {
                        this.selectOptionsPatients = result.payload.pats.map(
                            (patient: PatientPharma) => {
                                return { label: patient.nam, value: patient.id };
                            }
                        );
                    });
            }
        });
    }

    getDataForTheSelects(): void {
        this.institutionService.getInstitutions().then(() => {
            this.selectOptionsInsitutions =
                Object.assign([], this.institutionService.institutionList.getValue());

            this.selectOptionsInsitutions = this.selectOptionsInsitutions.sort((a, b) => a.label.localeCompare(b.label));

            if (this.selectOptionsInsitutions) {
                const hasEmptyValue = this.selectOptionsInsitutions.some(
                    (item) => item.value === ''
                );
                if (hasEmptyValue) {
                    this.selectOptionsInsitutions = this.selectOptionsInsitutions.filter(
                        (item) => item.value !== ''
                    );
                }
            }

            if (!this.typeGlobal) {
                this.form.controls['selectedInstitution'].setValue({ id: this.institutionService.getCurrentInstitution() });
            }
        });

        this.reportService
            .getRepParamsEditDataMedchanges(this.currentInstitution)
            .subscribe((result) => {
                this.selectOptionsPatients = result.payload.pats.map(
                    (patient: PatientPharma) => {
                        return { label: patient.nam, value: patient.id };
                    }
                );
            });
    }

    handlePreviewClick() {
        if (this.form.valid) {
            const { specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil } = this.getFormData();

            this.setInfoRP(specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil, true);

            this.reportService.handlePreviewClick(this.router, this.reportDataPatientIssues);

            this.logger.logAction(UserAction.Click, {
                message: 'Vista previa',
                ...this.reportDataPatientIssues.data
            });
        } else {
            if (this.form.value.selectedInstitution == '') {
                this.calls.openSnack('Debes seleccionar un centro');
            } else {
                this.calls.openSnack('Debes seleccionar un día de generación de plan');
            }
        }
    }

    getFormData(): {
        specificDay: string;
        startDate: string;
        endDate: string;
        selectedPatient: string;
        selectedOptionFrom: string;
        selectedOptionUntil: string;
    } {
        const specificDay = this.dateService.formatDateToString(
            this.form.get('specificDay')?.value
        );
        const startDate = this.dateService.formatDateToString(
            this.form.get('startDate')?.value
        );
        const endDate = this.dateService.formatDateToString(
            this.form.get('endDate')?.value
        );
        const selectedPatient = this.form.get('selectedPatient')?.value;
        const selectedOptionFrom = this.form.get('selectedOptionFrom')?.value;
        const selectedOptionUntil = this.form.get('selectedOptionUntil')?.value;

        return {
            specificDay,
            startDate,
            endDate,
            selectedPatient,
            selectedOptionFrom,
            selectedOptionUntil,
        };
    }

    handlePrintClick() {
        if (this.form.valid) {
            const { specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil } = this.getFormData();

            this.setInfoRP(specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil);
            this.reportService.handlePrintClick(this.reportDataPatientIssues);

            this.logger.logAction(UserAction.Click, {
                message: 'Impresión',
                ...this.reportDataPatientIssues.dataRenderReport
            });
        } else {
            if (this.form.value.selectedInstitution == '') {
                this.calls.openSnack('Debes seleccionar un centro');
            } else {
                this.calls.openSnack('Debes seleccionar un día de generación de plan');
            }
        }
    }

    handlePdfClick() {
        if (this.form.valid) {
            const { specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil } = this.getFormData();

            this.setInfoRP(specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil);
            this.reportService.handlePdfClick(this.reportDataPatientIssues);

            this.logger.logAction(UserAction.Click, {
                message: 'Exportar PDF',
                ...this.reportDataPatientIssues.dataDownloadPDF
            });
        } else {
            if (this.form.value.selectedInstitution == '') {
                this.calls.openSnack('Debes seleccionar un centro');
            } else {
                this.calls.openSnack('Debes seleccionar un día de generación de plan');
            }
        }
    }

    handleExcelClick() {
        if (this.form.valid) {
            const { specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil } = this.getFormData();

            this.setInfoRP(specificDay, startDate, endDate, selectedPatient, selectedOptionFrom, selectedOptionUntil);
            this.reportService.handleExcelClick(this.reportDataPatientIssues);

            this.logger.logAction(UserAction.Click, {
                message: 'Exportar Excel',
                ...this.reportDataPatientIssues.dataDownloadExcel
            });
        } else {
            if (this.form.value.selectedInstitution == '') {
                this.calls.openSnack('Debes seleccionar un centro');
            } else {
                this.calls.openSnack('Debes seleccionar un día de generación de plan');
            }
        }
    }

    setInfoRP(
        specificDay: string,
        startDate: string,
        endDate: string,
        selectedPatient: string,
        selectedOptionFrom: string,
        selectedOptionUntil: string,
        isPreview: boolean = false
    ): void {
        let startDateRP;
        let endDateRP;

        if (this.form.controls['intervalType'].value == 'specificDay') {
            startDateRP = this.dateService.transformToDateWithSpecificHours(specificDay, "00:00");
            endDateRP = this.dateService.transformDateTimeIncrementHours(specificDay);
        } else {
            startDateRP = this.dateService.transformToDateWithSpecificHours(
                startDate,
                selectedOptionFrom
            );
            endDateRP = this.dateService.transformToDateWithSpecificHours(
                endDate,
                selectedOptionUntil
            );
        }

        let currentInstitution;
        const dataForm = this.form.getRawValue();
        //@ts-ignore
        currentInstitution = dataForm.selectedInstitution.id;

        const rp = JSON.stringify({
            LocaleId: '3082',
            InstitutionId: String(currentInstitution),
            PatientId: selectedPatient ? String(selectedPatient) : null,
            DateFrom: startDateRP,
            DateUntil: endDateRP,
        });

        this.reportDataPatientIssues.data.rp = rp;
        if (isPreview) this.reportDataPatientIssues.data.dn = Utils.buildUniqueFileName + "_{0}.html";
        this.reportDataPatientIssues.dataDownloadPDF.rp = rp;
        this.reportDataPatientIssues.dataDownloadExcel.rp = rp;
        this.reportDataPatientIssues.dataRenderReport.rp = rp;

        const now = moment();

        this.reportDataPatientIssues.dataDownloadPDF.dn = this.reportDataPatientIssues.dataRenderReport.dn =
            `Reporte_de_incidencias_de_pacientes_${this.institutionService.getCurrentInstitutionShortName()}_${now.format('YYYYMMDD')}_${now.format('HHMM')}.pdf`;
        this.reportDataPatientIssues.dataDownloadExcel.dn = `Reporte_de_incidencias_de_pacientes_${this.institutionService.getCurrentInstitutionShortName()}_${now.format('YYYYMMDD')}_${now.format('HHMM')}.xls`;
    }

    toggleSelectPatient(): void {
        this.showPatientSelect = !this.showPatientSelect;
        if (!this.showPatientSelect) {
            this.form.patchValue({
                selectedPatient: '',
            });
        }
    }

    toggleSelectSpecificDay(): void {
        this.showSpecificDaySelect = !this.showSpecificDaySelect;
        if (!this.showSpecificDaySelect) {
            this.form.patchValue({
                specificDay: '',
            });
        }
    }

    toggleSelectIntervalDay(): void {
        this.showIntervalDay = !this.showIntervalDay;
        if (!this.showIntervalDay) {
            this.form.patchValue({
                startDate: '',
                endDate: '',
            });
        }
    }
}
